import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save } from "lucide-react";
import type {
  CreatePartyRequest,
  PartyDetailDto,
  PartyKind,
  PartyListItemDto,
  PartyType,
  UpdatePartyRequest,
} from "@/types/parties";
import { partyBasePath } from "@/types/parties";
import { partiesApi } from "@/api/parties";
import { ApiError } from "@/api/client";
import type {
  AccreditedInvestorStatus,
  AddressDto,
  BankAccountType,
  CounterpartyTaxFilingStatus,
  EntityTypeCode,
} from "@/types/counterparties";
import { EMPTY_ADDRESS } from "@/types/counterparties";
import { Button, Input, Label } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { useToast } from "@/components/ui/Toast";
import { useCreateParty, usePartyDetail, useUpdateParty } from "@/hooks/parties/useParties";

// Party read-flip (WS-A) — create + edit for Borrower/Seller parties
// (PersonEditPage conventions: dedicated page, masked sensitive fields
// never echoed — blank keeps the stored ciphertext). PartyKind is picked
// at create and immutable afterwards; the visible field sections follow it.

const ENTITY_TYPES: EntityTypeCode[] = [
  "LLC", "SCorp", "CCorp", "LP", "LLP", "Trust", "SoleProp", "Other",
];
const BANK_ACCOUNT_TYPES: BankAccountType[] = ["Checking", "Savings", "Other"];
const ACCREDITED_STATUSES: AccreditedInvestorStatus[] = [
  "Verified", "SelfDeclared", "NotApplicable", "Unknown",
];
const TAX_FILING_STATUSES: CounterpartyTaxFilingStatus[] = [
  "ScheduleC", "LLCDisregarded", "LLCPartnership", "SCorp", "CCorp", "Other",
];

interface PartyFormState {
  partyKind: PartyKind;
  email: string;
  secondaryEmail: string;
  phone: string;
  secondaryPhone: string;
  // person
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  dateOfBirth: string;
  ssn: string; // write-only
  // business
  legalName: string;
  dba: string;
  entityType: EntityTypeCode;
  stateOfFormation: string;
  dateOfFormation: string;
  ein: string; // write-only
  // address
  address: AddressDto;
  mailingDifferent: boolean;
  mailingAddress: AddressDto;
  // borrower preliminaries
  industryExperienceYears: string;
  estimatedNetWorth: string;
  estimatedLiquidity: string;
  preliminaryCreditScore: string;
  equityInjectionSource: string;
  // seller preliminaries
  accreditedInvestorStatus: AccreditedInvestorStatus | "";
  taxFilingStatus: CounterpartyTaxFilingStatus | "";
  originalSaleDate: string;
  // banking (write-only numbers)
  bankACHName: string;
  bankRoutingNumber: string;
  bankAccountNumber: string;
  bankAccountType: BankAccountType | "";
  generalNotes: string;
}

const EMPTY_FORM: PartyFormState = {
  partyKind: "Person",
  email: "",
  secondaryEmail: "",
  phone: "",
  secondaryPhone: "",
  firstName: "",
  middleName: "",
  lastName: "",
  suffix: "",
  dateOfBirth: "",
  ssn: "",
  legalName: "",
  dba: "",
  entityType: "LLC",
  stateOfFormation: "",
  dateOfFormation: "",
  ein: "",
  address: EMPTY_ADDRESS,
  mailingDifferent: false,
  mailingAddress: EMPTY_ADDRESS,
  industryExperienceYears: "",
  estimatedNetWorth: "",
  estimatedLiquidity: "",
  preliminaryCreditScore: "",
  equityInjectionSource: "",
  accreditedInvestorStatus: "",
  taxFilingStatus: "",
  originalSaleDate: "",
  bankACHName: "",
  bankRoutingNumber: "",
  bankAccountNumber: "",
  bankAccountType: "",
  generalNotes: "",
};

function detailToForm(p: PartyDetailDto): PartyFormState {
  return {
    partyKind: p.partyKind,
    email: p.email,
    secondaryEmail: p.secondaryEmail ?? "",
    phone: p.phone,
    secondaryPhone: p.secondaryPhone ?? "",
    firstName: p.firstName ?? "",
    middleName: p.middleName ?? "",
    lastName: p.lastName ?? "",
    suffix: p.suffix ?? "",
    dateOfBirth: p.dateOfBirth ?? "",
    ssn: "", // never echoed — blank keeps stored ciphertext
    legalName: p.legalName ?? "",
    dba: p.dba ?? "",
    entityType: p.entityType ?? "LLC",
    stateOfFormation: p.stateOfFormation ?? "",
    dateOfFormation: p.dateOfFormation ?? "",
    ein: "",
    address: p.address,
    mailingDifferent: !!p.mailingAddress,
    mailingAddress: p.mailingAddress ?? EMPTY_ADDRESS,
    industryExperienceYears: p.industryExperienceYears?.toString() ?? "",
    estimatedNetWorth: p.estimatedNetWorth?.toString() ?? "",
    estimatedLiquidity: p.estimatedLiquidity?.toString() ?? "",
    preliminaryCreditScore: p.preliminaryCreditScore?.toString() ?? "",
    equityInjectionSource: p.equityInjectionSource ?? "",
    accreditedInvestorStatus: p.accreditedInvestorStatus ?? "",
    taxFilingStatus: p.taxFilingStatus ?? "",
    originalSaleDate: p.originalSaleDate ?? "",
    bankACHName: p.bankACHName ?? "",
    bankRoutingNumber: "",
    bankAccountNumber: "",
    bankAccountType: p.bankAccountType ?? "",
    generalNotes: p.generalNotes ?? "",
  };
}

// F1 — save errors are honest + anchored: an email-specific server message
// ("A borrower already exists with this email address.") renders under the
// email input; everything else keeps the inline alert banner.
type SaveError = { field: "email"; message: string } | { general: string };

function numberOrNull(v: string): number | null {
  const t = v.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function PartyEditPage({ type }: { type: PartyType }) {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const toast = useToast();

  const base = partyBasePath(type);
  const noun = type === "Borrower" ? "borrower" : "seller";
  const title = type === "Borrower" ? "Borrowers" : "Sellers";

  const [form, setForm] = useState<PartyFormState>(EMPTY_FORM);
  const [error, setError] = useState<SaveError | null>(null);
  // On a create-time globally-unique-email conflict, the record that already
  // owns the email — offered as "use existing" instead of a dead-end error.
  const [emailMatch, setEmailMatch] = useState<PartyListItemDto | null>(null);

  const existing = usePartyDetail(type, editing ? id : undefined);
  const create = useCreateParty(type);
  const update = useUpdateParty(type, id ?? "");

  useBreadcrumbs(
    editing
      ? existing.data
        ? [
            { label: title, href: base },
            { label: existing.data.displayName, href: `${base}/${id}` },
            { label: "Edit" },
          ]
        : null
      : [
          { label: title, href: base },
          { label: `New ${noun}` },
        ],
  );

  useEffect(() => {
    if (existing.data) setForm(detailToForm(existing.data));
  }, [existing.data]);

  const set = <K extends keyof PartyFormState>(key: K, value: PartyFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const isPerson = form.partyKind === "Person";
  const requiredComplete =
    !!form.email && (isPerson ? !!form.firstName && !!form.lastName : !!form.legalName);

  const buildShared = (): Omit<CreatePartyRequest, "partyKind"> => ({
    email: form.email,
    secondaryEmail: form.secondaryEmail,
    phone: form.phone,
    secondaryPhone: form.secondaryPhone,
    firstName: form.firstName,
    middleName: form.middleName,
    lastName: form.lastName,
    suffix: form.suffix,
    dateOfBirth: form.dateOfBirth || null,
    ssn: form.ssn || null, // blank = keep stored value (never echoed)
    legalName: form.legalName,
    dba: form.dba,
    entityType: isPerson ? null : form.entityType,
    stateOfFormation: form.stateOfFormation,
    dateOfFormation: form.dateOfFormation || null,
    ein: form.ein || null,
    address: form.address,
    mailingAddress: form.mailingDifferent ? form.mailingAddress : null,
    industryExperienceYears: numberOrNull(form.industryExperienceYears),
    estimatedNetWorth: numberOrNull(form.estimatedNetWorth),
    estimatedLiquidity: numberOrNull(form.estimatedLiquidity),
    preliminaryCreditScore: numberOrNull(form.preliminaryCreditScore),
    equityInjectionSource: form.equityInjectionSource,
    accreditedInvestorStatus: form.accreditedInvestorStatus || null,
    taxFilingStatus: form.taxFilingStatus || null,
    originalSaleDate: form.originalSaleDate || null,
    bankACHName: form.bankACHName,
    bankRoutingNumber: form.bankRoutingNumber || null,
    bankAccountNumber: form.bankAccountNumber || null,
    bankAccountType: form.bankAccountType || null,
    generalNotes: form.generalNotes,
  });

  // Server save errors: an email-mentioning message (the field-specific
  // uniqueness 400) anchors at the email input; anything else stays in the
  // inline alert banner.
  const onSaveError = (e: Error) => {
    if (/email/i.test(e.message)) {
      setError({ field: "email", message: e.message });
      const el = document.getElementById("party-email");
      el?.scrollIntoView({ block: "center" });
      el?.focus({ preventScroll: true });
    } else {
      setError({ general: e.message });
    }
  };

  // Create-only: the party registry enforces a globally-unique email — on a
  // conflict, find the record that already owns it (same type) and offer it
  // instead of the dead-end field error. Mirrors the New Deal wizard's inline
  // create; here there is no deal to attach to, so "Use existing" navigates to
  // the found party's detail page. A different-type collision (no same-type
  // match) degrades to today's plain field error.
  const onCreateError = async (e: Error) => {
    const message =
      e instanceof ApiError ? [e.message, ...e.errors].join(" ") : e.message;
    if (/email/i.test(message) && /(exist|already|use|taken)/i.test(message)) {
      const email = form.email.trim();
      try {
        const page = await partiesApi.list(type, { search: email, limit: 5 });
        const match = page.data.find(
          (p) => p.email.toLowerCase() === email.toLowerCase(),
        );
        if (match) {
          setEmailMatch(match);
          return;
        }
      } catch {
        /* fall through to the plain field error */
      }
    }
    onSaveError(e);
  };

  const onSave = () => {
    setError(null);
    setEmailMatch(null);
    if (!requiredComplete) {
      setError({
        general: isPerson
          ? "Email, first name, and last name are required."
          : "Email and legal name are required.",
      });
      return;
    }

    if (!editing) {
      const body: CreatePartyRequest = { partyKind: form.partyKind, ...buildShared() };
      create.mutate(body, {
        onSuccess: (created) => {
          toast.success(`${type} ${created.displayName} created.`);
          navigate(`${base}/${created.id}`);
        },
        onError: onCreateError,
      });
      return;
    }

    const body: UpdatePartyRequest = {
      ...buildShared(),
      clearMailingAddress: !form.mailingDifferent,
    };
    update.mutate(body, {
      onSuccess: (updated) => {
        toast.success(`${type} updated.`);
        navigate(`${base}/${updated.id}`);
      },
      onError: onSaveError,
    });
  };

  const isPending = create.isPending || update.isPending;

  return (
    <>
      <PageHeader
        eyebrow={`Parties · ${type}`}
        title={editing ? `Edit ${noun}` : `New ${noun}`}
        description={
          editing
            ? "Sensitive fields (SSN / EIN / bank numbers) are never shown — leave them blank to keep the stored values."
            : undefined
        }
        actions={
          <>
            <Button size="sm" variant="secondary" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button size="sm" onClick={onSave} loading={isPending} disabled={!requiredComplete}>
              <Save size={14} strokeWidth={1.5} aria-hidden />{" "}
              {editing ? "Save changes" : `Save ${noun}`}
            </Button>
          </>
        }
      />

      {error && "general" in error && (
        <div
          className="bg-status-error/10 border-l-[3px] border-status-error text-small text-status-error p-space-3 rounded-md mb-space-3"
          role="alert"
        >
          {error.general}
        </div>
      )}

      {emailMatch && (
        <div className="flex items-center justify-between gap-space-2 rounded-md border border-gold bg-gold/5 p-space-3 mb-space-3">
          <span className="text-small text-onyx">
            A {noun} already exists with this email:{" "}
            <span className="font-semibold">{emailMatch.displayName}</span>.
          </span>
          <Button size="sm" onClick={() => navigate(`${base}/${emailMatch.id}`)}>
            Use existing
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-space-4 max-w-4xl">
        {!editing && (
          <FormSection title="Kind">
            <div>
              <Label htmlFor="party-kind">Party kind</Label>
              <Select
                id="party-kind"
                value={form.partyKind}
                onChange={(e) => set("partyKind", e.target.value as PartyKind)}
              >
                <option value="Person">Person</option>
                <option value="Business">Business</option>
              </Select>
              <p className="text-caption text-gray-mid mt-space-1 m-0">
                The kind is fixed once the party is created.
              </p>
            </div>
          </FormSection>
        )}

        <FormSection title="Contact">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <Text
              label="Email (portal login)"
              id="party-email"
              value={form.email}
              onChange={(v) => {
                set("email", v);
                setError((prev) => (prev && "field" in prev ? null : prev));
                setEmailMatch(null);
              }}
              type="email"
              required
              error={error && "field" in error ? error.message : undefined}
            />
            <Text label="Secondary email" id="party-email2" value={form.secondaryEmail} onChange={(v) => set("secondaryEmail", v)} type="email" />
            <Text label="Phone" id="party-phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <Text label="Secondary phone" id="party-phone2" value={form.secondaryPhone} onChange={(v) => set("secondaryPhone", v)} />
          </div>
        </FormSection>

        {isPerson ? (
          <FormSection title="Person">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
              <Text label="First name" id="party-first" value={form.firstName} onChange={(v) => set("firstName", v)} required />
              <Text label="Last name" id="party-last" value={form.lastName} onChange={(v) => set("lastName", v)} required />
              <Text label="Middle name" id="party-middle" value={form.middleName} onChange={(v) => set("middleName", v)} />
              <Text label="Suffix" id="party-suffix" value={form.suffix} onChange={(v) => set("suffix", v)} />
              <Text label="Date of birth" id="party-dob" value={form.dateOfBirth} onChange={(v) => set("dateOfBirth", v)} type="date" />
              <Text
                label="SSN"
                id="party-ssn"
                value={form.ssn}
                onChange={(v) => set("ssn", v)}
                placeholder={editing ? existing.data?.ssnMasked ?? "Leave blank to keep" : ""}
              />
            </div>
          </FormSection>
        ) : (
          <FormSection title="Business">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
              <Text label="Legal name" id="party-legal" value={form.legalName} onChange={(v) => set("legalName", v)} required />
              <Text label="DBA" id="party-dba" value={form.dba} onChange={(v) => set("dba", v)} />
              <div>
                <Label htmlFor="party-entity-type">Entity type</Label>
                <Select
                  id="party-entity-type"
                  value={form.entityType}
                  onChange={(e) => set("entityType", e.target.value as EntityTypeCode)}
                >
                  {ENTITY_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
              </div>
              <Text label="State of formation" id="party-sof" value={form.stateOfFormation} onChange={(v) => set("stateOfFormation", v)} />
              <Text label="Date of formation" id="party-dof" value={form.dateOfFormation} onChange={(v) => set("dateOfFormation", v)} type="date" />
              <Text
                label="EIN"
                id="party-ein"
                value={form.ein}
                onChange={(v) => set("ein", v)}
                placeholder={editing ? existing.data?.einMasked ?? "Leave blank to keep" : ""}
              />
            </div>
          </FormSection>
        )}

        <FormSection title="Address">
          <AddressFields
            idPrefix="party-addr"
            value={form.address}
            onChange={(a) => set("address", a)}
          />
          <label className="flex items-center gap-space-2 text-small text-onyx mt-space-2">
            <input
              type="checkbox"
              checked={form.mailingDifferent}
              onChange={(e) => set("mailingDifferent", e.target.checked)}
            />
            Mailing address is different
          </label>
          {form.mailingDifferent && (
            <div className="mt-space-2">
              <AddressFields
                idPrefix="party-mail"
                value={form.mailingAddress}
                onChange={(a) => set("mailingAddress", a)}
              />
            </div>
          )}
        </FormSection>

        {type === "Borrower" ? (
          <FormSection title="Borrower preliminaries">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
              <Text label="Industry experience (yrs)" id="party-exp" value={form.industryExperienceYears} onChange={(v) => set("industryExperienceYears", v)} type="number" />
              <Text label="Estimated net worth ($)" id="party-networth" value={form.estimatedNetWorth} onChange={(v) => set("estimatedNetWorth", v)} type="number" />
              <Text label="Estimated liquidity ($)" id="party-liquidity" value={form.estimatedLiquidity} onChange={(v) => set("estimatedLiquidity", v)} type="number" />
              <Text label="Preliminary credit score" id="party-credit" value={form.preliminaryCreditScore} onChange={(v) => set("preliminaryCreditScore", v)} type="number" />
              <Text label="Equity injection source" id="party-equity" value={form.equityInjectionSource} onChange={(v) => set("equityInjectionSource", v)} />
            </div>
          </FormSection>
        ) : (
          <FormSection title="Seller preliminaries">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
              <div>
                <Label htmlFor="party-accredited">Accredited investor</Label>
                <Select
                  id="party-accredited"
                  value={form.accreditedInvestorStatus}
                  onChange={(e) => set("accreditedInvestorStatus", e.target.value as AccreditedInvestorStatus | "")}
                >
                  <option value="">—</option>
                  {ACCREDITED_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </div>
              <div>
                <Label htmlFor="party-taxfiling">Tax filing status</Label>
                <Select
                  id="party-taxfiling"
                  value={form.taxFilingStatus}
                  onChange={(e) => set("taxFilingStatus", e.target.value as CounterpartyTaxFilingStatus | "")}
                >
                  <option value="">—</option>
                  {TAX_FILING_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </Select>
              </div>
              <Text label="Original sale date" id="party-saledate" value={form.originalSaleDate} onChange={(v) => set("originalSaleDate", v)} type="date" />
            </div>
          </FormSection>
        )}

        <FormSection title="Banking">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
            <Text label="ACH name" id="party-ach" value={form.bankACHName} onChange={(v) => set("bankACHName", v)} />
            <div>
              <Label htmlFor="party-bank-type">Account type</Label>
              <Select
                id="party-bank-type"
                value={form.bankAccountType}
                onChange={(e) => set("bankAccountType", e.target.value as BankAccountType | "")}
              >
                <option value="">—</option>
                {BANK_ACCOUNT_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </Select>
            </div>
            <Text
              label="Routing #"
              id="party-routing"
              value={form.bankRoutingNumber}
              onChange={(v) => set("bankRoutingNumber", v)}
              placeholder={editing ? existing.data?.bankRoutingNumberMasked ?? "Leave blank to keep" : ""}
            />
            <Text
              label="Account #"
              id="party-account"
              value={form.bankAccountNumber}
              onChange={(v) => set("bankAccountNumber", v)}
              placeholder={editing ? existing.data?.bankAccountNumberMasked ?? "Leave blank to keep" : ""}
            />
          </div>
        </FormSection>

        <FormSection title="Notes">
          <Text label="General notes" id="party-notes" value={form.generalNotes} onChange={(v) => set("generalNotes", v)} />
        </FormSection>
      </div>
    </>
  );
}

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white-pure border border-border-light rounded-lg p-space-4">
      <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow mb-space-3 m-0">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Text({
  label,
  id,
  value,
  onChange,
  type = "text",
  required,
  placeholder,
  error,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  error?: string;
}) {
  return (
    <div>
      <Label htmlFor={id}>
        {label}
        {required && <span className="text-status-error"> *</span>}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {error && (
        <p className="text-caption text-status-error mt-space-1 m-0" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function AddressFields({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: AddressDto;
  onChange: (a: AddressDto) => void;
}) {
  const set = (patch: Partial<AddressDto>) => onChange({ ...value, ...patch });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
      <Text label="Street address" id={`${idPrefix}-street1`} value={value.streetAddress1} onChange={(v) => set({ streetAddress1: v })} />
      <Text label="Street address 2" id={`${idPrefix}-street2`} value={value.streetAddress2 ?? ""} onChange={(v) => set({ streetAddress2: v })} />
      <Text label="City" id={`${idPrefix}-city`} value={value.city} onChange={(v) => set({ city: v })} />
      <Text label="State" id={`${idPrefix}-state`} value={value.state} onChange={(v) => set({ state: v })} />
      <Text label="Zip" id={`${idPrefix}-zip`} value={value.zip} onChange={(v) => set({ zip: v })} />
      <Text label="Country" id={`${idPrefix}-country`} value={value.country} onChange={(v) => set({ country: v })} />
    </div>
  );
}

export function BorrowerEditPage() {
  return <PartyEditPage type="Borrower" />;
}

export function SellerEditPage() {
  return <PartyEditPage type="Seller" />;
}
